import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

export interface ReputationControlProps {
  /** Reputation score 0–100 */
  value: number;
  /** Show numeric value alongside the bar */
  showValue?: boolean;
  /** Bar width in px */
  width?: number;
  /** Bar height in px */
  height?: number;
  /** Compact inline style for cards */
  size?: 'sm' | 'md';
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

function getColor(value: number): string {
  const v = clamp(value, 0, 100);
  if (v > 85) return '#10B981';   // green
  if (v >= 60) return '#EAB308';  // yellow
  if (v >= 45) return '#F97316';  // orange
  return '#EF4444';               // red
}

function getLabel(value: number): string {
  const v = clamp(value, 0, 100);
  if (v > 85) return 'Excellent';
  if (v >= 60) return 'Good';
  if (v >= 45) return 'Fair';
  return 'Low';
}

export default function ReputationControl({
  value,
  showValue = true,
  width = 80,
  height = 8,
  size,
}: ReputationControlProps) {
  const w = size === 'sm' ? 48 : size === 'md' ? 64 : width;
  const h = size === 'sm' ? 6 : size === 'md' ? 7 : height;
  const clamped = clamp(typeof value === 'number' ? value : 0, 0, 100);
  const color = getColor(clamped);
  const label = getLabel(clamped);

  return (
    <Tooltip title={`Reputation: ${label} (${clamped})`} arrow placement="top">
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: w,
            height: h,
            borderRadius: 1,
            backgroundColor: 'rgba(0,0,0,0.08)',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${clamped}%`,
              height: '100%',
              backgroundColor: color,
              borderRadius: 1,
              transition: 'width 0.2s ease',
            }}
          />
        </Box>
        {showValue && (
          <Typography variant="caption" sx={{ fontSize: size === 'sm' ? '0.7rem' : '0.75rem', fontWeight: 600, color: color }}>
            {clamped}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}
